import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createAppKit } from '@reown/appkit/react';
import { metadata, wagmiAdapter, projectId } from './config';
import App from './App';
import './index.css';

// Solana Wallet Adapter imports
import {
  ConnectionProvider,
  WalletProvider
} from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { 
  TorusWalletAdapter, 
  LedgerWalletAdapter
} from '@solana/wallet-adapter-wallets';
import { getSolanaRpcEndpoints } from './lib/solana';
import React, { useState, useEffect, Suspense } from 'react';

// Type declaration for React Router future flags
declare global {
  interface Window {
    REACT_ROUTER_FUTURE_FLAGS: {
      v7_startTransition: boolean;
      v7_relativeSplatPath: boolean;
    };
  }
}

// Tell React Router to apply v7 behaviors globally
// This is required before any React Router imports
window.REACT_ROUTER_FUTURE_FLAGS = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// CORS proxy for fallback
const CORS_PROXY = 'https://corsproxy.io/?';

// Create Query Client with optimized settings for production
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 10000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    },
  },
});

// Configure Solana with proxy fallback
const solanaEndpoints = getSolanaRpcEndpoints();
const endpoint = solanaEndpoints[0]; // Try the first endpoint by default
const proxyEndpoint = `${CORS_PROXY}${encodeURIComponent(endpoint)}`; // Use proxy as fallback

const solanaConfig = {
  commitment: 'confirmed' as const,
  confirmTransactionInitialTimeout: 60000
};

// Add more wallets for better compatibility
const wallets = [
  new TorusWalletAdapter(),
  new LedgerWalletAdapter()
];

// Custom ConnectionProvider with error handling
const CustomConnectionProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [currentEndpoint, setCurrentEndpoint] = useState(endpoint);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    const testConnection = async () => {
      try {
        // Test if primary Helius endpoint works
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'getHealth'
          })
        });
        
        const data = await response.json();
        
        if (!response.ok || !data || data.error) {
          throw new Error(`Helius endpoint responded with an error: ${data?.error?.message || 'Unknown error'}`);
        }
      } catch (error) {
        // Try CORS proxy as fallback
        try {
          setCurrentEndpoint(proxyEndpoint);
          
          const proxyResponse = await fetch(proxyEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jsonrpc: '2.0',
              id: 1,
              method: 'getHealth'
            })
          });
          
          if (!proxyResponse.ok) {
            throw new Error('Proxy connection also failed');
          }
          
        } catch (proxyError) {
          setConnectionError('Could not establish a reliable connection to Solana network. Some features may not work properly.');
        }
      } finally {
        setConnectionAttempted(true);
      }
    };

    testConnection();
  }, []);

  if (!connectionAttempted) {
    return (
      <div className="flex items-center justify-center h-screen bg-quantum-dark text-quantum-light">
        <div className="text-center p-4">
          <div className="animate-spin w-12 h-12 border-4 border-quantum-primary border-t-transparent rounded-full mb-4 mx-auto"></div>
          <p>Connecting to Solana network...</p>
        </div>
      </div>
    );
  }

  return (
    <ConnectionProvider endpoint={currentEndpoint} config={solanaConfig}>
      {connectionError && (
        <div className="fixed top-0 left-0 w-full bg-red-500 text-white p-2 text-center z-50">
          {connectionError}
        </div>
      )}
      {children}
    </ConnectionProvider>
  );
};

// Loading component for suspense fallback
const Loading = () => (
  <div className="flex items-center justify-center h-screen bg-quantum-dark text-quantum-light">
    <div className="text-center p-4">
      <div className="animate-spin w-12 h-12 border-4 border-quantum-primary border-t-transparent rounded-full mb-4 mx-auto"></div>
      <p>Loading...</p>
    </div>
  </div>
);

// Initialize AppKit with production optimizations
const appKit = createAppKit({
  adapters: [wagmiAdapter],
  projectId,
  networks: wagmiAdapter.networks as any, // Type assertion to avoid linter errors
  metadata,
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#00F6FF',
    '--w3m-logo-image-url': 'https://quantumgenesis.io/favicon.svg',
    '--w3m-background-color': '#000B1F',
    '--w3m-color-fg-1': '#E2F8FF',
    '--w3m-color-fg-2': 'rgba(226, 248, 255, 0.7)',
    '--w3m-border-radius-master': '12px'
  } as any, // Type assertion to avoid linter errors
  features: {
    analytics: true,
    connectMethodsOrder: ['wallet', 'qr', 'extension'] as any, // Type assertion to avoid linter errors
    swaps: false,
    onramp: false,
    send: false
  } as any // Type assertion to avoid linter errors
});

// Get root element
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found! Add <div id="root"></div> to your HTML');
}

// Create React root and render app with error boundary
try {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <Suspense fallback={<Loading />}>
        <WagmiProvider config={wagmiAdapter.wagmiConfig}>
          <QueryClientProvider client={queryClient}>
            <CustomConnectionProvider>
              <WalletProvider wallets={wallets} autoConnect>
                <WalletModalProvider>
                  <App />
                </WalletModalProvider>
              </WalletProvider>
            </CustomConnectionProvider>
          </QueryClientProvider>
        </WagmiProvider>
      </Suspense>
    </StrictMode>
  );
} catch (error) {
  // Fallback error display
  rootElement.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: #000B1F;
      color: #E2F8FF;
      padding: 20px;
      text-align: center;
    ">
      <h1 style="color: #00F6FF; margin-bottom: 20px;">Something went wrong</h1>
      <p>Please try refreshing the page. If the problem persists, contact support.</p>
    </div>
  `;
}